/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import { clamp } from "@utils/utils";
import { BiquadMagnitude } from "@wbaudio/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { AreaLayer } from "../../layers/areas.ts";
import { LinesLayer } from "../../layers/lines.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { FormatHz } from "../../utils/format.ts";
import { SampleDomain } from "../../utils/sample.ts";
import { CreateFrequencyChart, FrequencyDisplay, QScale } from "./frequencyChart.ts";

export interface FilterCurveProps {
  frequency: number;
  q: number;
  filterType: string;
  onDrag?: (patch: { frequency: number; q: number }) => void;
}

export function ResponsePoints(type: string, fc: number, q: number) {
  const { fMin, fMax, mMax } = FrequencyDisplay;
  return SampleDomain(
    (freq) => clamp(BiquadMagnitude(type, fc, q, freq), 0, mMax),
    [fMin, fMax],
    80,
    true,
  );
}

function CreateFilterChart(
  container: HTMLElement,
  mode: ChartMode,
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const fill = AreaLayer();
  const curve = LinesLayer();
  const handles = mode === "editor" ? HandlesLayer({ y: QScale(), onDrag }) : null;
  const chart = CreateFrequencyChart(container, {
    mode,
    layers: [fill, curve, ...(handles ? [handles] : [])],
  });
  return { chart, fill, curve, handles, destroy: chart.destroy };
}

export function FilterCurve(props: FilterCurveProps) {
  const mode: ChartMode = props.onDrag ? "editor" : "inline";
  const onDrag = useLatest(props.onDrag);
  const { ref, chart } = useChart(
    (container) =>
      CreateFilterChart(
        container,
        mode,
        (_, p) => onDrag.current?.({ frequency: Math.round(p.x), q: parseFloat(p.y.toFixed(2)) }),
      ),
    [mode],
  );

  useEffect(() => {
    if (!chart) return;
    const points = ResponsePoints(props.filterType, props.frequency, props.q);
    chart.fill.set([{ id: "fill", points }]);
    chart.curve.set([{ id: "curve", points }]);
    chart.handles?.set([
      { id: "fc", x: props.frequency, y: props.q, radius: 5, label: FormatHz(props.frequency) },
    ]);
    chart.chart.render();
  }, [chart, props.frequency, props.q, props.filterType]);

  return <div ref={ref} />;
}
